import React from "react";
import { CircleCheck, CircleHelp, History, PauseCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AppColorVariant } from "../../theme/AppColorVariant";
import {
  GraphLibraryTypeLabels,
  GraphLibraryVersioningStatus,
  VersioningState,
  isVersioningUndetermined,
  versioningPresentation,
} from "../../models/ClientConfiguration/VersioningState";

interface VersioningSummaryChipProps {
  libraries?: GraphLibraryVersioningStatus[];
}

/**
 * One-line versioning verdict for a whole client configuration, so a list can be scanned without
 * opening each configuration.
 *
 * Ranked by what actually blocks work rather than by the versioning setting alone. Libraries are
 * routinely created with versioning on, so counting that would mark almost every configuration and
 * discriminate nothing; a suspended library is the state that stops files moving, and an overridden
 * one is versioned yet migrating perfectly well.
 */
const VersioningSummaryChip: React.FC<VersioningSummaryChipProps> = ({ libraries }) => {
  if (!libraries || libraries.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const suspendedCount = libraries.filter((library) => library.migrationSuspended === true).length;
  const undeterminedCount = libraries.filter((library) =>
    isVersioningUndetermined(library.versioningState),
  ).length;
  const versionedCount = libraries.filter(
    (library) => library.versioningState === VersioningState.Enabled,
  ).length;

  let variant: AppColorVariant = "success";
  let Icon: React.ElementType = CircleCheck;
  let label = "Migrating";

  if (suspendedCount > 0) {
    variant = "danger";
    Icon = PauseCircle;
    label = `${suspendedCount} suspended`;
  } else if (undeterminedCount > 0) {
    variant = "neutral";
    Icon = CircleHelp;
    label = `${undeterminedCount} unconfirmed`;
  } else if (versionedCount > 0) {
    // Versioned with nothing suspended. Usually an override letting these through, but a library no
    // client has reported suspension for lands here too, so the label states only what is known.
    variant = "warning";
    Icon = History;
    label = `${versionedCount} versioned`;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={variant}>
            <Icon className="size-3" />
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <ul className="flex flex-col gap-0.5">
            {libraries.map((library) => (
              <li key={library.libraryType}>
                <span className="font-medium">
                  {GraphLibraryTypeLabels[library.libraryType] ?? library.libraryType}
                </span>
                : {versioningPresentation(library).label}
                {library.migrationSuspended === true && " — suspended"}
                {library.overrideApplied === true && " — overridden"}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default VersioningSummaryChip;
